import { api } from "../api.js";
import { t, translatePage } from "../i18n.js";
import { formatBytes } from "../format.js";
import { icon } from "../icon.js";
import { confirmDialog, promptDialog } from "./dialog.js";
import { renderMenu } from "./menu.js";
import { renderLoading } from "./loading.js";
import { createGuard } from "../busy.js";
import { escapeHtml } from "../escape.js";

// Renders a file list as cards (type tile, name, meta line, size, and a
// per-row overflow menu holding Edit note / Delete) plus a drop zone that
// accepts several files at once, dropped or browsed. `path` is either
// `/trips/{id}/files` or `/locations/{id}/files` - both share the same
// list/upload/delete shape.
//
// Picking does *not* upload. A pick lands in a "Ready to upload" list under the
// zone and goes to the server only when the Upload button is pressed, one file
// at a time. The reason is the note field and the visibility selector below the
// zone: uploading on pick meant they were controls you noticed after they could
// no longer be applied, so the only way to set either was to fix the file
// afterwards through its row menu. Now they are read when the button is
// pressed, which is what they look like they do. It also means an oversized
// file is refused before anything is sent, and a pick can be taken back.
//
// The trip-level list mixes trip files with files attached to a location, so a
// row carrying `location_title` shows it (".file-card__source"): one flat list
// sorted by upload date, where only the location-attached rows are labelled. The API
// leaves location_title null on the location-level list, so the same template renders
// there unlabelled without needing a mode - on a location's own page every file
// belongs to that location and saying so on each row would be noise.
//
// Read-only mode: pass `readOnly: true` for a list with no add row and no
// per-row menu - the location *view* page, where editing lives behind its Edit
// button. That caller already has the rows (it fetches them to decide whether
// to render its card at all), so it passes them as `rows` and the component
// skips the request rather than asking for the same list twice.
//
// Staging mode: pass `path: null` plus a `staged` array to collect picks
// in memory instead of uploading them. Nothing is fetched and nothing is
// POSTed, ever; each pick is pushed as { file, note, visibility } and the list
// renders from the File objects (name + size). The location create page uses
// this so a brand-new location can carry files, then uploads them itself once
// the location ID exists - a multipart upload can't be part of the JSON create
// request, so post-create is the only option regardless (see todo.md).
//
// Staged picks are the create page's *only* list, so they sit in the main list
// and keep a per-row menu for their own note and visibility - that page has no
// Upload button to read a batch-level choice at. The pending picks described
// above are the other half of the same idea: same { file, note, visibility }
// shape, rendered by the same row template, but held in their own list under
// the zone and given their note and visibility by the batch when the button is
// pressed.
//
// Visibility (Stage 14 Milestone 7, reworked in its follow-up): pass
// `shared: true` when the trip has somebody else on it. Left off, nothing about
// visibility is rendered at all — on a solo trip the distinction cannot mean
// anything, and offering it would be asking a question with one possible answer.
//
// When it *is* on, the list splits into two labelled groups: files everyone on
// the trip can see, and files only you can. The grouping is what tells you which
// is which, so no row carries a visibility marker and no menu carries a
// visibility *state*. Each row's menu instead offers the one move available to
// it — "make this visible to only me" for a shared file, "make this visible to
// everyone" for a private one.
//
// The first version of this did the opposite: one flat list, a lock badge per
// personal row, and a radio group in each menu. Two things were wrong with it.
// Finding your own document meant reading down a list of everyone's, which is
// the thing sections fix. And passing `activeValue` to renderMenu makes the
// trigger echo the selected item, so every row's ⋮ was labelled "Everyone on the
// trip" — the same mistake as the members row in Milestone 3, whose menu was
// labelled with its own role. A per-row ⋮ should say nothing.
//
// The value still travels on every upload either way; the server defaults it,
// and a trip that later gains a member finds its existing files already
// trip-visible.
let sectionSeq = 0;

export async function renderFileList(container, path, { staged, rows: given, readOnly = false, shared = false } = {}) {
  const isStaging = !path;
  // The visibility a new upload gets, remembered across a batch and across the
  // re-render that follows one — picking "personal" and then dropping three
  // files should not silently revert between them.
  let uploadVisibility = "trip";
  // The note for the next upload. Survives the re-render the same way, but is
  // cleared once it has been used: a note is about one document, so inheriting
  // it silently on the next upload would be wrong.
  let pendingNote = "";
  // Picks waiting for the Upload button, in the live-path case. Same shape as
  // `staged`, but note and visibility are left off here: they are filled in
  // from the batch controls when the button is pressed, so holding a per-row
  // copy would only be a second answer to the same question.
  let pending = [];
  // One id per component instance for the pending list's label, so two file
  // lists on one page (the location editor has the location's, the trip tab has
  // the trip's) do not both point aria-labelledby at the same node.
  const pendingTitleId = `file-pending-${sectionSeq++}`;
  // A selector rather than a node: render() rebuilds the whole subtree, so
  // whatever a handler wanted focused no longer exists by the time it could be
  // focused. Consumed at the end of render().
  let focusAfter = null;
  // One in-flight flag for the whole component rather than per render: the
  // button is a fresh node after every render(), so `elements` is a function -
  // busy.js resolves it at invocation time for exactly this case.
  const uploadGuarded = createGuard({ elements: () => container.querySelector(".file-upload__submit") }).wrap(upload);
  // Errors survive the re-render that shows them: render() rebuilds the whole
  // subtree, so the paragraph a handler wrote into is gone by the time the user
  // would read it.
  let errors = [];
  // Nothing is fetched in staging mode, and nothing when the caller already
  // has the rows (the location view page fetches them itself to decide whether
  // to render its card at all - refetching here would be a second request for
  // the same list).
  if (!isStaging && !given) renderLoading(container);
  let files = isStaging ? [] : given ?? (await api.get(path));

  // Full rebuild on every call, list and add-form together - not a
  // partial re-render that reuses a persistent form across calls. The
  // latter is exactly what caused a real duplicate-submit bug elsewhere
  // in this app (see location-editor-page.js's renderLinksList()/
  // bindLinkForm() split): a form.addEventListener() call inside a
  // function that's itself re-invoked by its own submit handler stacks
  // one more listener on the same node every time. A full rebuild
  // sidesteps that class of bug entirely, since the form node is never
  // reused across calls - every render() gets a fresh one with exactly
  // one listener.
  //
  // The file input is deliberately not `required`. It's hidden (the visible
  // control is the label around it), and a hidden invalid control can't be
  // focused to show a validation bubble - so the browser blocked the submit
  // event outright and the click did nothing at all, leaving only "The
  // invalid form control with name='file' is not focusable" in the console.
  // The submit handler below reports an empty pick instead.
  function render() {
    container.innerHTML = `
      <div class="file-list">
        <p class="file-list__summary" hidden></p>
        <div class="file-sections"></div>
        <p class="file-list-empty" data-i18n="files.empty" hidden></p>
        ${
          readOnly
            ? ""
            : `
        <p class="file-list__error" role="alert" hidden></p>
        <div class="file-upload">
        <!-- tabindex="-1" so upload() can hand the focus back here once the
             button it was on is gone; it stays out of the tab order. -->
        <label class="file-drop" tabindex="-1">
          <input type="file" name="file" multiple hidden data-i18n-aria-label="common.uploadFile" />
          <span class="file-drop__icon">${icon("upload")}</span>
          <span class="file-drop__text">
            <span class="file-drop__title file-drop__title--wide" data-i18n="files.dropTitle"></span>
            <span class="file-drop__title file-drop__title--narrow" data-i18n="files.dropTitleNarrow"></span>
            <span class="file-drop__hint file-drop__hint--wide" data-i18n="files.dropHint"></span>
            <span class="file-drop__hint file-drop__hint--narrow" data-i18n="files.dropHintNarrow"></span>
          </span>
          <span class="btn btn-secondary file-drop__browse" aria-hidden="true" data-i18n="files.browse"></span>
        </label>
        <!-- The picks waiting for the button. Hidden until there is one, so a
             first visit looks the way it always did: a zone and its options. -->
        <div class="file-upload__pending" ${pending.length ? "" : "hidden"}>
          <p class="file-upload__pending-title" id="${pendingTitleId}" data-i18n="files.pendingTitle"></p>
          <ul class="files files--pending" aria-labelledby="${pendingTitleId}"></ul>
        </div>
        <!-- Everything below applies to the pending batch, which is what the
             hint says once for the group rather than once per control. A note
             typed here lands on every file in the batch: it is a title for a
             document, so the usual case is one file, and per-file notes are
             still editable from each row's menu afterwards. -->
        <div class="file-upload__options">
          <p class="file-upload__hint" data-i18n="files.uploadOptionsHint"></p>
          <label class="file-upload__field">
            <span data-i18n="files.noteLabel"></span>
            <input type="text" name="uploadNote" autocomplete="off" data-i18n-placeholder="files.notePlaceholder" />
          </label>
          ${
            shared
              ? `<div class="file-upload__field">
            <span id="file-visibility-label" data-i18n="files.visibility.label"></span>
            <div class="setting-choices" role="radiogroup" aria-labelledby="file-visibility-label">
              <label class="setting-choice">
                <input type="radio" name="uploadVisibility" value="trip" />
                <span data-i18n="files.visibility.trip"></span>
              </label>
              <label class="setting-choice">
                <input type="radio" name="uploadVisibility" value="personal" />
                <span data-i18n="files.visibility.personal"></span>
              </label>
            </div>
          </div>`
              : ""
          }
        </div>
        <!-- The commit. Hidden rather than disabled with nothing pending: a
             permanently dead button under an empty zone says less than no
             button at all. -->
        <button type="button" class="btn btn-primary file-upload__submit" ${pending.length ? "" : "hidden"}></button>
        </div>
        `
        }
      </div>
    `;
    translatePage(container);

    const sections = container.querySelector(".file-sections");
    const emptyState = container.querySelector(".file-list-empty");
    const rows = isStaging ? staged : files;
    emptyState.hidden = rows.length > 0;

    // Count and total size, above the list. The heading itself belongs to the
    // caller (a tab, or an .editor-card), so this says "3 files - 459 KB"
    // rather than repeating the word the heading already carries. Plural via
    // t()'s count argument, so German gets its own two forms too.
    const summary = container.querySelector(".file-list__summary");
    summary.hidden = rows.length === 0;
    if (rows.length) {
      const total = rows.reduce((sum, row) => sum + (isStaging ? row.file.size : row.size_bytes), 0);
      summary.textContent = t("files.summary", { total: formatBytes(total) }, rows.length);
    }

    // Grouped only when the trip is shared. Indices are carried along because
    // staging mode addresses rows by position in `staged`, and grouping would
    // otherwise renumber them.
    const indexed = rows.map((row, i) => ({ row, i }));
    const visibilityOf = (row) => (isStaging ? row.visibility || "trip" : row.visibility);
    const groups = shared
      ? [
          { key: "trip", titleKey: "files.visibility.trip", entries: indexed.filter((e) => visibilityOf(e.row) === "trip") },
          { key: "personal", titleKey: "files.personal", entries: indexed.filter((e) => visibilityOf(e.row) === "personal") },
        ].filter((g) => g.entries.length > 0)
      : [{ key: null, titleKey: null, entries: indexed }];

    groups.forEach((group) => {
      const wrapper = document.createElement("div");
      wrapper.className = "file-section";
      wrapper.dataset.visibility = group.key || "";
      // A label rather than a heading: this is a grouping inside one card, not
      // a new section of the document, and inserting an h3 under a tab whose
      // only heading is the page h1 would put a hole in the outline that
      // headings.spec.js is right to object to. aria-labelledby gives the list
      // the same name a heading would have.
      const titleId = `file-section-${group.key || "all"}-${sectionSeq++}`;
      wrapper.innerHTML = group.titleKey
        ? `<p class="file-section__title" id="${titleId}">${escapeHtml(t(group.titleKey))}</p>
           <ul class="files" aria-labelledby="${titleId}"></ul>`
        : `<ul class="files"></ul>`;
      sections.appendChild(wrapper);
      const list = wrapper.querySelector(".files");
      group.entries.forEach(({ row, i }) => renderRow(list, row, i, isStaging ? "staged" : "stored"));
    });

    // The pending picks, in their own list under the zone rather than mixed
    // into the groups above: their visibility is not decided until the button
    // is pressed, so there is no group they could honestly sit in, and they are
    // not part of what the summary line counts either - that sentence is about
    // what the trip has, not about what this tab is holding.
    const pendingList = container.querySelector(".files--pending");
    if (pendingList) pending.forEach((row, i) => renderRow(pendingList, row, i, "pending"));

    function renderRow(list, row, i, kind) {
      const isPick = kind !== "stored";
      // One view model for both modes: a staged pick is a File object
      // (name/size/type and nothing else), an uploaded one is an API row, and
      // every difference between them belongs here rather than in two copies
      // of the card template.
      const view = isPick
        ? { filename: row.file.name, size: row.file.size, contentType: row.file.type, note: row.note, locationTitle: null, href: null, visibility: row.visibility || "trip", isMine: true }
        : { filename: row.filename, size: row.size_bytes, contentType: row.content_type, note: row.note, locationTitle: row.location_title, href: row.download_url, visibility: row.visibility, isMine: row.is_mine };

      // The note wins the title when there is one, and the filename drops to
      // the meta line. A note is the only readable name a file uploaded as
      // "5d2ffd5f-b621-41d9-9b10-173d5a72f860.png" will ever have, and it used
      // to render as a small italic afterthought behind exactly that string.
      const title = view.note ? escapeHtml(view.note) : filenameHtml(view.filename);

      const sep = (variant = "") => `<span class="file-card__sep${variant ? ` file-card__sep--${variant}` : ""}" aria-hidden="true">·</span>`;

      // The size appears twice in the markup and exactly once on screen: the
      // standalone column is hidden under 640px, this copy shown, and vice
      // versa above it. Two nodes rather than one because CSS can move a box
      // within its own flex parent but cannot move it into a different one,
      // and on a phone there is no room for a size column beside the name.
      // The inline size carries its own separator (hidden with it above 640px,
      // where the standalone size column takes over) - otherwise desktop showed
      // a stray leading dot in front of the filename.
      const metaHead =
        `<span class="file-card__size file-card__size--inline">${formatBytes(view.size)}</span>` + (view.note ? `${sep("size")}${filenameHtml(view.filename)}` : "");

      // Then the desktop-only "No note" hint, and the location this file is
      // attached to (trip-level lists only). Order matters for the punctuation:
      // every separator here has to have something visible in front of it at
      // its own breakpoint, or it strands a dot at the start of the line.
      //   mobile:  [size · filename] / [location on its own line]
      //   desktop: [filename or "No note" · location]  (size is the column)
      // So "No note" needs no separator of its own - it is only ever first -
      // while the location's is desktop-only, hidden where the location wraps.
      // No visibility marker on the row: it lives in a labelled group that
      // already says which kind it is, and repeating that per row was noise in
      // a list that is mostly filenames.
      // "No note" is a statement about a row that could have one of its own.
      // A pending pick cannot: its note comes from the batch field below, which
      // has not been read yet, so the hint would be answering a question the
      // row does not own. Staged rows keep it - those do have a per-row note.
      const metaTail =
        (view.note || kind === "pending" ? "" : `<span class="file-card__nonote">${escapeHtml(t("files.noNote"))}</span>`) +
        (view.locationTitle ? `${sep("source")}<span class="file-card__source">${escapeHtml(view.locationTitle)}</span>` : "");

      const body = `
        <span class="file-card__tile">${icon(tileIconName(view.contentType))}</span>
        <span class="file-card__text">
          <span class="file-card__name">${title}</span>
          <span class="file-card__meta">${metaHead}${metaTail}</span>
        </span>
      `;

      const li = document.createElement("li");
      li.className = "file-card";
      // The whole card is the download link, which is also what clears the
      // 44px tap target without a min-height propping it up - the old row was
      // a bare filename link measuring 22px. A staged pick has nothing to
      // download yet, so there its body is a plain span.
      li.innerHTML = `
        ${view.href ? `<a class="file-card__body" href="${view.href}" target="_blank" rel="noopener">${body}</a>` : `<span class="file-card__body">${body}</span>`}
        <span class="file-card__size">${formatBytes(view.size)}</span>
        ${readOnly ? "" : `<span class="file-actions"></span>`}
      `;

      // One overflow menu per row rather than a bare delete icon: a note can
      // now be changed after upload (PATCH /api/files/{id}), so the row has
      // two actions, and two icons competing beside a filename is exactly the
      // pile-up this row already has too much of. renderMenu's action-item
      // mode exists for this - these are things the menu does, not a
      // selection it holds.
      if (readOnly) {
        list.appendChild(li);
        return;
      }
      // A pending pick has exactly one thing you can do to it - take it back -
      // and .icon-remove is what this app puts beside a row for that (see the
      // link and date lists in location-editor-page.js). A menu holding one
      // item would be two taps for the same move. Note and visibility are the
      // batch controls below, not per-row here.
      if (kind === "pending") {
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "icon-remove";
        remove.setAttribute("aria-label", t("common.remove"));
        remove.innerHTML = icon("x");
        remove.addEventListener("click", () => {
          pending.splice(i, 1);
          render();
        });
        li.querySelector(".file-actions").replaceWith(remove);
        list.appendChild(li);
        return;
      }
      renderMenu(li.querySelector(".file-actions"), {
        // Vertical, not the horizontal ellipsis the tab bar's "More" uses: this
        // one belongs to the row it sits in, and the vertical form is what a
        // per-row overflow reads as everywhere else.
        iconName: "ellipsis-vertical",
        chevron: false,
        triggerClass: "file-actions__trigger",
        ariaLabel: "files.actions",
        // Every item here is an action, which is what keeps the trigger silent:
        // renderMenu echoes the *selected* item when given an activeValue, so a
        // menu holding a selection cannot also have a label-less trigger.
        //
        // Visibility appears as the one move available to this file rather than
        // as a pair of states — the group it sits in already says which state
        // that is. Offered only for the uploader's own files: an editor may
        // rename or delete a shared file, but who reads someone else's document
        // is not theirs to decide, and the server refuses it too.
        items: [
          ...(shared && view.isMine
            ? [
                view.visibility === "personal"
                  ? { value: "share", label: t("files.makeShared"), iconName: "users", action: true }
                  : { value: "hide", label: t("files.makePersonal"), iconName: "lock", action: true },
              ]
            : []),
          { value: "note", label: t("files.editNote"), iconName: "pencil", action: true },
          { value: "delete", label: t(isPick ? "common.remove" : "common.delete"), iconName: "trash-2", action: true, danger: true },
        ],
        onSelect: async (action) => {
          if (action === "share" || action === "hide") {
            const next = action === "share" ? "trip" : "personal";
            if (isStaging) {
              staged[i].visibility = next;
            } else {
              const updated = await api.put(`/files/${row.id}/visibility`, { visibility: next });
              files = files.map((f) => (f.id === updated.id ? updated : f));
            }
            render();
            return;
          }
          if (action === "note") {
            const note = await promptDialog({
              messageKey: "files.notePrompt",
              value: row.note || "",
              placeholderKey: "files.notePlaceholder",
            });
            // null means cancelled; "" means "clear it", which is a real
            // answer and has to reach the server.
            if (note === null) return;
            if (isStaging) {
              staged[i].note = note || null;
            } else {
              const updated = await api.patch(`/files/${row.id}`, { note });
              files = files.map((f) => (f.id === updated.id ? updated : f));
            }
            render();
            return;
          }

          if (isStaging) {
            staged.splice(i, 1);
            render();
            return;
          }
          if (!(await confirmDialog({ messageKey: "files.deleteConfirm" }))) return;
          await api.delete(`/files/${row.id}`);
          files = files.filter((f) => f.id !== row.id);
          render();
        },
      });
      list.appendChild(li);
    }

    // Read-only mode stops here: no add row, so nothing below this line has a
    // node to bind to.
    if (readOnly) return;

    const drop = container.querySelector(".file-drop");
    const fileInput = drop.querySelector('input[type="file"]');
    const errorEl = container.querySelector(".file-list__error");

    // Tracked on every keystroke and reflected back after a render, the same
    // way the visibility choice is: staging a pick re-renders the whole
    // subtree, so a half-typed note would otherwise vanish the moment you
    // picked the file it was for. It is read - and cleared - by upload().
    const noteInput = container.querySelector('[name="uploadNote"]');
    if (noteInput) noteInput.value = pendingNote;
    noteInput?.addEventListener("input", () => {
      pendingNote = noteInput.value;
    });

    // The selector reflects the remembered choice and writes back to it, rather
    // than being read out of the DOM at submit time: the value has to survive
    // the re-render that staging a pick causes, and a drop gesture never
    // touches this control at all.
    if (shared) {
      const current = container.querySelector(`[name="uploadVisibility"][value="${uploadVisibility}"]`);
      if (current) current.checked = true;
      container.querySelectorAll('[name="uploadVisibility"]').forEach((input) => {
        input.addEventListener("change", () => {
          if (input.checked) uploadVisibility = input.value;
        });
      });
    }

    if (errors.length) {
      // One line per file that failed, so a batch reports which of its members
      // was the problem instead of failing anonymously.
      errorEl.textContent = errors.join(" ");
      errorEl.hidden = false;
      errors = [];
    }

    // Picking stages, it does not upload. The old "Choose a file first." case
    // still cannot happen: an empty pick is simply ignored by stage().
    fileInput.addEventListener("change", () => stage(fileInput.files));

    // The drag half. dragover has to preventDefault on every event or the
    // browser navigates to the dropped file instead of handing it over, and
    // dragleave fires when the pointer crosses onto a *child* of the zone too,
    // hence the contains() guard - without it the highlight flickers off as
    // soon as the pointer reaches the icon or the label text.
    drop.addEventListener("dragover", (e) => {
      e.preventDefault();
      drop.classList.add("file-drop--over");
    });
    drop.addEventListener("dragleave", (e) => {
      if (!drop.contains(e.relatedTarget)) drop.classList.remove("file-drop--over");
    });
    drop.addEventListener("drop", (e) => {
      e.preventDefault();
      drop.classList.remove("file-drop--over");
      stage(e.dataTransfer?.files);
    });

    const submitBtn = container.querySelector(".file-upload__submit");
    if (pending.length) {
      // Plural through t()'s count argument, so German gets its own two forms -
      // and the count is the only place the size of the batch is stated, since
      // .file-list__summary counts what the server has.
      submitBtn.textContent = t("files.upload", {}, pending.length);
      submitBtn.addEventListener("click", uploadGuarded);
    }

    // What the last handler asked to be focused, now that render() has replaced
    // every node it could have held a reference to.
    if (focusAfter) {
      container.querySelector(focusAfter)?.focus();
      focusAfter = null;
    }
  }

  // Everything a pick can be: one file or several, dropped or browsed. Nothing
  // leaves the browser here.
  function stage(fileList) {
    const picked = [...(fileList || [])];
    if (!picked.length) return;
    errors = [];

    // Captured here only for staging mode, which has no button to read it at
    // later, and cleared once used: a note is about one document, so inheriting
    // it silently on the next pick would be wrong. The live path leaves it
    // alone - upload() reads it when the button is pressed.
    const batchNote = pendingNote.trim();

    for (const file of picked) {
      // Checked here as well as by the server, which answers an oversized
      // upload with a 413 whose body is about multipart parsing rather than
      // about this file - see maxFileUploadBytes in internal/httpapi/files.go.
      // Refused at pick time now, so an oversized file never reaches the wire.
      if (file.size > MAX_UPLOAD_BYTES) {
        errors.push(t("files.tooLarge", { name: file.name, limit: formatBytes(MAX_UPLOAD_BYTES) }));
        continue;
      }
      // unshift, not push: the list is newest-first (the API sorts by
      // uploaded_at DESC), so appending put a new file at the *bottom* until
      // the next load moved it to the top. Found by the spec below, which
      // asserted the order before and after a reload.
      if (isStaging) {
        staged.unshift({ file, note: batchNote || null, visibility: uploadVisibility });
        continue;
      }
      pending.unshift({ file });
    }
    if (isStaging) pendingNote = "";
    // The button is the next thing to press and, at 324px, can be below the
    // fold - so hand it the focus rather than leaving it on a file input that
    // has already done its job.
    if (pending.length) focusAfter = ".file-upload__submit";

    // One re-render for the whole batch, which also replaces the file input -
    // so picking the same file twice in a row still fires `change` the second
    // time, and any errors collected above are printed by the next render().
    render();
  }

  // The commit. Sequential rather than parallel so a failure belongs to a named
  // file, and so a dozen dropped files don't open a dozen simultaneous
  // requests. A double press is dropped by uploadGuarded above.
  async function upload() {
    const batch = [...pending];
    if (!batch.length) return;
    const drop = container.querySelector(".file-drop");
    container.querySelector(".file-list__error").hidden = true;
    errors = [];

    drop.setAttribute("aria-busy", "true");
    drop.classList.add("file-drop--busy");

    // Read now, not when the file was picked - the whole point of the button.
    const batchNote = pendingNote.trim();

    for (const entry of batch) {
      try {
        const formData = new FormData();
        formData.append("file", entry.file);
        formData.append("visibility", uploadVisibility);
        if (batchNote) formData.append("note", batchNote);
        const res = await fetch(`/api${path}`, { method: "POST", body: formData, credentials: "same-origin" });
        const created = await res.json();
        if (!res.ok) throw new Error(created.error || t("common.error"));
        files.unshift(created);
        // Only what actually landed leaves the pending list, so a partly failed
        // batch can be retried by pressing the button again rather than picking
        // the same files a second time.
        pending = pending.filter((p) => p !== entry);
      } catch (err) {
        errors.push(`${entry.file.name}: ${err.message || t("common.error")}`);
      }
    }

    // The note belongs to the batch that consumed it, so it is cleared only
    // once that batch is actually gone - a retry keeps the note it was typed
    // for. Same for the focus: back to the zone when there is nothing left to
    // upload, and left on the button when there is.
    if (!pending.length) {
      pendingNote = "";
      focusAfter = ".file-drop";
    }
    render();
  }

  render();
}

// The server's own limit (maxFileUploadBytes in internal/httpapi/files.go).
// Duplicated deliberately: the alternative is asking the API for it, and a
// number that has to match on both sides is better as a named constant with a
// pointer at the other one than as an extra round trip.
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

// Which tile a file gets, from the content type the server sniffed on upload
// (never the client-declared one - see internal/httpapi/files.go). Three
// buckets, because that is as much as a 36px tile can say: a picture, a
// document, or something else.
function tileIconName(contentType) {
  const type = (contentType || "").split(";")[0].trim();
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("text/") || type === "application/pdf") return "file-text";
  return "file";
}

// A filename split into stem and extension, so the stem can ellipsize while
// the extension stays visible: "5d2ffd5f-b621-4... .png" rather than
// "5d2ffd5f-b621-41d9-9b10-173..." with the one part that says what the file
// *is* cut off. CSS-only (the stem gets overflow: hidden, the extension
// flex: none), so there is no measuring and no resize listener.
function filenameHtml(filename) {
  const dot = filename.lastIndexOf(".");
  const hasExt = dot > 0 && dot < filename.length - 1;
  const stem = hasExt ? filename.slice(0, dot) : filename;
  const ext = hasExt ? filename.slice(dot) : "";
  return `<span class="file-card__filename"><span class="file-card__stem">${escapeHtml(stem)}</span>${ext ? `<span class="file-card__ext">${escapeHtml(ext)}</span>` : ""}</span>`;
}
